// Real-time voice disguiser for the anonymous complaint recorder.
//
// Pitch-shifts the microphone signal so the whistleblower's voice cannot be
// recognised by colleagues reviewing the complaint. It is a granular
// delay-line pitch shifter built entirely from native Web Audio nodes (two
// delay lines cross-faded with modulated delay times) — no AudioWorklet and no
// external dependency, so it runs anywhere MediaRecorder is available.
//
// This is disguise, not cryptographic anonymisation: it defeats casual
// voice recognition, not forensic voiceprint analysis. Because the shift runs
// in the browser before encoding, the *original* voice never leaves the
// device — only the disguised signal is uploaded.
//
// Adapted from the well-known Web Audio "Jungle" pitch-shifter technique
// (Chris Wilson, MIT-licensed).

const DELAY_TIME = 0.1; // seconds — grain length
const FADE_TIME = 0.05; // seconds — cross-fade window between grains
const BUFFER_TIME = 0.1; // seconds — modulation period

// Disguise depth: the grain playback ratio is (1 - PITCH_OFFSET), so 0.3 lowers
// the pitch to 0.7× (~6 semitones down). Direction is always downward (see
// createDelayTimeBuffer); only the magnitude matters. High enough to defeat
// casual voice recognition, low enough to stay intelligible.
const PITCH_OFFSET = 0.3;

function createFadeBuffer(context, activeTime, fadeTime) {
  const length1 = activeTime * context.sampleRate;
  const length2 = (activeTime - 2 * fadeTime) * context.sampleRate;
  const length = length1 + length2;
  const buffer = context.createBuffer(1, length, context.sampleRate);
  const p = buffer.getChannelData(0);

  const fadeLength = fadeTime * context.sampleRate;
  const fadeIndex1 = fadeLength;
  const fadeIndex2 = length1 - fadeLength;

  for (let i = 0; i < length1; ++i) {
    let value;
    if (i < fadeIndex1) {
      value = Math.sqrt(i / fadeLength);
    } else if (i >= fadeIndex2) {
      value = Math.sqrt(1 - (i - fadeIndex2) / fadeLength);
    } else {
      value = 1;
    }
    p[i] = value;
  }

  for (let i = length1; i < length; ++i) {
    p[i] = 0;
  }

  return buffer;
}

function createDelayTimeBuffer(context, activeTime, fadeTime) {
  const length1 = activeTime * context.sampleRate;
  const length2 = (activeTime - 2 * fadeTime) * context.sampleRate;
  const length = length1 + length2;
  const buffer = context.createBuffer(1, length, context.sampleRate);
  const p = buffer.getChannelData(0);

  // Ascending 0 -> 1 delay ramp over the active window, then flat through the
  // cross-fade tail. A lengthening delay slows playback of each grain, which
  // lowers the pitch — the direction we want for the disguise.
  for (let i = 0; i < length1; ++i) {
    p[i] = i / length1;
  }
  for (let i = length1; i < length; ++i) {
    p[i] = 0;
  }

  return buffer;
}

/**
 * Builds the pitch-shifting graph and returns its input and output nodes.
 * Callers connect their source to `input` and read from `output`.
 *
 * @param {AudioContext} context
 * @returns {{ input: GainNode, output: GainNode, start: () => void, stop: () => void }}
 */
export function createVoiceDisguise(context) {
  const input = context.createGain();
  const output = context.createGain();

  const modulationTime = BUFFER_TIME;
  // Downward-shift ramp only: the disguise always lowers pitch, so we never
  // need the upward buffer. `AudioBufferSourceNode.buffer` is write-once, so it
  // is assigned here exactly once and never reassigned.
  const shiftDownBuffer = createDelayTimeBuffer(context, modulationTime, FADE_TIME);
  const fadeBuffer = createFadeBuffer(context, modulationTime, FADE_TIME);

  // Two grain paths, each = delay line whose delay time is modulated by a
  // sawtooth, cross-faded so the grain wrap is inaudible.
  const mod1 = context.createBufferSource();
  const mod2 = context.createBufferSource();
  mod1.buffer = shiftDownBuffer;
  mod2.buffer = shiftDownBuffer;
  mod1.loop = true;
  mod2.loop = true;

  const mod1Gain = context.createGain();
  const mod2Gain = context.createGain();

  const delay1 = context.createDelay();
  const delay2 = context.createDelay();

  // Sawtooth grain ramps modulate each delay line's delay time.
  mod1.connect(mod1Gain);
  mod2.connect(mod2Gain);
  mod1Gain.connect(delay1.delayTime);
  mod2Gain.connect(delay2.delayTime);

  // Cross-fade envelopes for the two grains (offset by half the period).
  const fade1 = context.createBufferSource();
  const fade2 = context.createBufferSource();
  fade1.buffer = fadeBuffer;
  fade2.buffer = fadeBuffer;
  fade1.loop = true;
  fade2.loop = true;

  const mix1 = context.createGain();
  const mix2 = context.createGain();
  mix1.gain.value = 0;
  mix2.gain.value = 0;

  fade1.connect(mix1.gain);
  fade2.connect(mix2.gain);

  input.connect(delay1);
  input.connect(delay2);
  delay1.connect(mix1);
  delay2.connect(mix2);
  mix1.connect(output);
  mix2.connect(output);

  const setOffset = (mult) => {
    const delayModulation = DELAY_TIME * Math.abs(mult);
    mod1Gain.gain.value = delayModulation;
    mod2Gain.gain.value = delayModulation;
    delay1.delayTime.value = DELAY_TIME;
    delay2.delayTime.value = DELAY_TIME;
  };

  return {
    input,
    output,
    start() {
      setOffset(PITCH_OFFSET);
      const t = context.currentTime;
      const t2 = t + modulationTime - FADE_TIME;
      mod1.start(t);
      mod2.start(t2);
      fade1.start(t);
      fade2.start(t2);
    },
    stop() {
      [mod1, mod2, fade1, fade2].forEach((node) => {
        try {
          node.stop();
        } catch {
          // already stopped
        }
      });
    },
  };
}
